// Shared Sentry trace state for an integrated payment attempt.
//
// The payment spans two components -- `CheckoutActionsV2` (which fires the
// `create` call and drives the provider/device leg) and
// `CheckoutComponent/index` (which runs the status poll loop). To land the
// create call, the provider sale, every status poll, the tRPC handlers and the
// DBOS workflow in ONE Sentry trace, we capture the trace context
// (sentry-trace + baggage strings) when the payment starts and rejoin it for
// each subsequent leg.
//
// EVERY leg after `create` MUST go through `startPaymentSpan`, which wraps the
// work in `Sentry.continueTrace`. That is not just about trace stitching -- it
// is what keeps the span from being silently discarded:
//
//   * `continueTrace` clears the active span on a forked scope, so the span it
//     starts is a SEGMENT ROOT whose parent is the remote span id from the
//     header. Segment roots are sent to Sentry the moment they end.
//   * A span started as a CHILD (e.g. via `withActiveSpan`) is only sent as
//     part of its segment root's transaction, which is assembled when that root
//     ends. `SentrySpan._onSpanEnded` returns early for non-segment spans, and
//     the root's own child collection keeps only spans that have already
//     finished. So a child still running when its root ends is dropped in
//     total silence -- no warning, no `recordDroppedEvent`.
//
// The payment root span therefore stays short-lived (it ends with `create`).
// Nothing is allowed to depend on its lifetime. The payment's real duration is
// reported by the `settled` span emitted from `endPaymentTrace`, which is
// created only once the payment is already over -- so it is complete the
// instant it exists and can never be dropped.
//
// Keyed by checkoutId, which is known before the workflow id exists.

import * as Sentry from "@sentry/vue";

export type PaymentTraceOutcome = "ok" | "cancelled" | "failed" | "abandoned";

export type PaymentTraceContext = {
  sentryTrace?: string;
  baggage?: string;
};

type PaymentTraceEntry = PaymentTraceContext & {
  /** Seconds since epoch, matching Sentry's `startTime` units. */
  startedAt: number;
  /**
   * Recorded once `create` returns. Not known when the trace starts -- the
   * payment row does not exist until then -- so the root and `create` spans
   * carry only `checkout_id`, and everything after them carries both.
   */
  paymentId?: number;
};

const entries = new Map<string, PaymentTraceEntry>();

export const setPaymentTraceContext = (
  checkoutId: string,
  context: PaymentTraceContext,
): void => {
  entries.set(checkoutId, { ...context, startedAt: Date.now() / 1000 });
};

export const getPaymentTraceContext = (
  checkoutId: string,
): PaymentTraceContext | undefined => entries.get(checkoutId);

/**
 * Attach the payment id to the trace once `create` has returned it, so the
 * `settled` span -- which is built after the fact, long after the id is out of
 * scope at every call site -- can be found by payment rather than only by
 * checkout. No-op if the payment's trace was never started.
 */
export const setPaymentTraceId = (
  checkoutId: string,
  paymentId: number,
): void => {
  const entry = entries.get(checkoutId);
  if (entry) entry.paymentId = paymentId;
};

/**
 * Run `callback` inside a span that rejoins the payment's trace as a segment
 * root. Use this for every leg of the payment after `create` -- the provider
 * sale, the provider-response attach, each status poll.
 */
export const startPaymentSpan = <T>(
  checkoutId: string,
  spanOptions: Parameters<typeof Sentry.startSpan>[0],
  callback: (span: Sentry.Span) => T,
): T => {
  const entry = entries.get(checkoutId);
  if (!entry?.sentryTrace) {
    // No captured context (payment started before this module saw it). Running
    // the work untraced is better than failing the payment for a span.
    return Sentry.startSpan(spanOptions, callback);
  }

  return Sentry.continueTrace(
    { sentryTrace: entry.sentryTrace, baggage: entry.baggage },
    () => Sentry.startSpan(spanOptions, callback),
  );
};

/**
 * Close out the payment's trace, emitting a `settled` span covering the whole
 * payment. Safe to call more than once and from any exit path -- the second
 * call is a no-op.
 */
export const endPaymentTrace = (
  checkoutId: string,
  outcome: PaymentTraceOutcome = "ok",
): void => {
  const entry = entries.get(checkoutId);
  if (!entry) return;

  entries.delete(checkoutId);
  if (!entry.sentryTrace) return;

  // Created after the fact with an explicit start time: already finished when
  // it is born, so it is sent immediately and cannot be dropped.
  Sentry.continueTrace(
    { sentryTrace: entry.sentryTrace, baggage: entry.baggage },
    () =>
      Sentry.startInactiveSpan({
        name: "checkout.integrated_payment.settled",
        op: "checkout.payment",
        startTime: entry.startedAt,
        attributes: {
          checkout_id: checkoutId,
          ...(entry.paymentId === undefined
            ? {}
            : { payment_id: entry.paymentId }),
          payment_outcome: outcome,
          payment_duration_ms: Math.round(
            Date.now() - entry.startedAt * 1000,
          ),
        },
      }).end(),
  );
};

export const clearPaymentTraceContext = (
  checkoutId: string,
  outcome: PaymentTraceOutcome = "abandoned",
): void => endPaymentTrace(checkoutId, outcome);
